import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { dateTime } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { btnGhost, btnPrimary } from "@/components/Brand";

const MILESTONES = ["selection_received", "editing_complete", "printing_complete", "delivered"];

export default function ClientPortal() {
  const { t } = useI18n();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get("/client/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="client-portal-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-5 lg:px-12 py-12 lg:py-16">
        <p className="eyebrow mb-3">{t("tracking")}</p>
        <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium">{t("my_projects")}</h1>

        {!projects ? (
          <div className="flex justify-center py-24 text-[#707070]"><Loader2 className="animate-spin" /></div>
        ) : projects.length === 0 ? (
          <p className="mt-12 border border-[#2A2A2A] bg-[#0B0B0B] p-12 text-center text-sm text-[#A8A8A8]" data-testid="portal-empty">
            No galleries yet.
          </p>
        ) : (
          <div className="mt-12 space-y-6" data-testid="portal-projects">
            {projects.map((p) => (
              <div key={p.project_id} data-testid={`portal-project-${p.project_id}`} className="border border-[#2A2A2A] bg-[#0F0F0F] p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono-accent text-xs text-[#D4AF37]">{p.code}</p>
                    <h2 className="mt-1 text-2xl tracking-tight">{p.title}</h2>
                    <p className="mt-1 text-xs text-[#707070]">
                      {p.edit_selected}/{p.edit_quota} {t("edits")} · {p.print_selected} {t("prints")}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {p.status !== "submitted" && (
                      <Link data-testid={`portal-open-${p.project_id}`} to={`/g/${p.share_token}`} className={btnPrimary}>
                        {t("open_gallery")} <ArrowRight size={13} />
                      </Link>
                    )}
                    <Link data-testid={`portal-summary-${p.project_id}`} to={`/g/${p.share_token}/summary`} className={btnGhost}>
                      {t("view_summary")}
                    </Link>
                  </div>
                </div>
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-4 gap-3">
                  {MILESTONES.map((m) => {
                    const done = !!p.milestones?.[m];
                    return (
                      <div key={m} className={`border p-3.5 ${done ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A]"}`}>
                        <span className={`flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase ${done ? "text-[#D4AF37]" : "text-[#707070]"}`}>
                          {done && <Check size={12} />} {t(`milestone_${m}`)}
                        </span>
                        <span className="mt-1.5 block font-mono-accent text-[10px] text-[#707070]">
                          {done ? dateTime(p.milestones[m]) : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
